import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useSeo } from "../lib/seo";
import { VENTURE_MUSIC, VENTURE_FILM } from "../lib/data";

const ventures = [
  {
    name: "Maajja",
    tag: "Independent Music · Co-founded 2021",
    image: VENTURE_MUSIC,
    description:
      "An independent music label and platform co-founded with A.R. Rahman in 2021, dedicated to South Asian indie artists. Maajja was founded on the principle that artists should retain creative control and master rights. Its flagship release \"Enjoy Enjaami\" surpassed one billion streams and became a generational anthem — a vindication of an artist-first model that is now widely studied across the industry.",
    bullets: [
      "Over 1B+ streams on flagship release",
      "Artist-first masters & rights model",
      "Global distribution & sync partnerships",
    ],
    span: "md:col-span-8",
  },
  {
    name: "Ideal Entertainment",
    tag: "Film & Content Production",
    image: VENTURE_FILM,
    description:
      "A film and content production company producing culturally specific, internationally minded projects. Recent credits include She Came Back (2024), Galaxy Built on Hope (2022), the immersive LeMusk experience, and the A.R. Rahman feature 99 Songs. A new development slate is currently in production — focused on diaspora narratives that travel without losing their cultural specificity.",
    bullets: [
      "Producer: She Came Back, Galaxy Built on Hope, 99 Songs, LeMusk",
      "Hybrid-language and cross-border financing",
      "Active 2025 development slate",
    ],
    span: "md:col-span-4",
  },
  {
    name: "Ideal Group",
    tag: "Parent Organization",
    image: null,
    description:
      "The parent organization under which Ideal Entertainment operates. Ideal Group provides the long-term capital, operational backbone, and partnership infrastructure that allow the entertainment arm to pursue ambitious, patient creative work.",
    bullets: [],
    span: "md:col-span-12",
  },
];

export default function Ventures() {
  useSeo({
    title: "Shaji Nada | Ventures — Maajja, Ideal Entertainment, Ideal Group",
    description:
      "Ventures founded and led by Shaji Nada — Maajja (co-founded with A.R. Rahman, 2021), Ideal Entertainment, and Ideal Group. Building infrastructure for South Asian independent music and film.",
    canonicalPath: "/ventures",
  });

  return (
    <div data-testid="ventures-page" className="pt-32 md:pt-40 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="overline mb-6">Ventures</p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.98] max-w-5xl" data-testid="ventures-h1">
          The infrastructure behind <span className="italic text-[var(--accent)]">a global wave.</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-white/60 max-w-3xl font-light leading-relaxed">
          Three vehicles, one thesis: champion South Asian independent voices on the global stage — and build the systems that let them stay there.
        </p>
      </section>

      <section className="mt-24 md:mt-32 max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8">
        {ventures.map((v, idx) => (
          <motion.article
            key={v.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: idx * 0.1 }}
            className={`${v.span} group relative border border-white/10 hover:border-[var(--accent)]/40 transition-colors duration-500`}
            data-testid={`venture-${v.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {v.image && (
              <div className="aspect-[16/10] overflow-hidden bg-[#1A1A1A]">
                <img
                  src={v.image}
                  alt={`${v.name} — venture by Shaji Nada`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.03]"
                />
              </div>
            )}
            <div className="p-8 md:p-10">
              <p className="overline mb-4">{v.tag}</p>
              <h2 className="font-serif-display text-4xl md:text-5xl text-white mb-5">{v.name}</h2>
              <p className="text-white/70 font-light leading-relaxed max-w-2xl">{v.description}</p>
              {v.bullets.length > 0 && (
                <ul className="mt-6 space-y-2">
                  {v.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-white/80 text-sm">
                      <span className="text-[var(--accent)] mt-0.5">—</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.article>
        ))}
      </section>
    </div>
  );
}
